import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../Utils/Axios";
import { resolveMediaUrl } from "../../Utils/media";
import "./AdminBlogs.css";

const initialForm = {
  title: "",
  excerpt: "",
  content: "",
  authorName: "System Admin",
  category: "",
  tags: "",
  isPublished: true,
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [selectedCoverFile, setSelectedCoverFile] = useState(null);
  const [previewCoverUrl, setPreviewCoverUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");

  const loadBlogs = async () => {
    try {
      const res = await api.get("/api/admin/blogs");
      setBlogs(res.data?.blogs || []);
    } catch (err) {
      toast.error("Failed to load blogs");
    }
  };

  useEffect(() => {
    loadBlogs();
  }, []);

  useEffect(() => {
    return () => {
      if (previewCoverUrl && previewCoverUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewCoverUrl);
      }
    };
  }, [previewCoverUrl]);

  const parsedTags = useMemo(
    () =>
      form.tags
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    [form.tags]
  );

  const stats = useMemo(() => {
    const total = blogs.length;
    const published = blogs.filter((item) => item.isPublished).length;
    const draft = total - published;
    return { total, published, draft };
  }, [blogs]);

  const submitLabel = editingId ? "Update Blog" : "Publish Blog";
  const previewReadMins = Math.max(
    1,
    Math.ceil((form.content.trim().split(/\s+/).filter(Boolean).length || 0) / 200)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim()) {
      toast.error("Title, excerpt and content are required");
      return;
    }

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("title", form.title);
      payload.append("excerpt", form.excerpt);
      payload.append("content", form.content);
      payload.append("authorName", form.authorName);
      payload.append("category", form.category);
      payload.append("isPublished", String(form.isPublished));
      payload.append("tags", parsedTags.join(","));
      if (selectedCoverFile) {
        payload.append("coverImageFile", selectedCoverFile);
      }

      if (editingId) {
        await api.put(`/api/admin/blogs/${editingId}`, payload, { timeout: 60000 });
        toast.success("Blog updated successfully");
      } else {
        await api.post("/api/admin/blogs", payload, { timeout: 60000 });
        toast.success("Blog created successfully");
      }

      setForm(initialForm);
      if (previewCoverUrl && previewCoverUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewCoverUrl);
      }
      setPreviewCoverUrl("");
      setSelectedCoverFile(null);
      setEditingId("");
      loadBlogs();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save blog");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (blog) => {
    setEditingId(blog._id);
    setForm({
      title: blog.title || "",
      excerpt: blog.excerpt || "",
      content: blog.content || "",
      authorName: blog.authorName || "System Admin",
      category: blog.category || "",
      tags: (blog.tags || []).join(", "),
      isPublished: blog.isPublished !== false,
    });
    setSelectedCoverFile(null);
    setPreviewCoverUrl(resolveMediaUrl(blog.coverImage) || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/blogs/${id}`);
      toast.success("Blog deleted");
      if (editingId === id) {
        setEditingId("");
        setForm(initialForm);
        setSelectedCoverFile(null);
        setPreviewCoverUrl("");
      }
      loadBlogs();
    } catch (err) {
      toast.error("Failed to delete blog");
    }
  };

  const handleCoverFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedCoverFile(file);
    if (previewCoverUrl && previewCoverUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewCoverUrl);
    }
    setPreviewCoverUrl(URL.createObjectURL(file));
  };

  const previewImage = resolveMediaUrl(previewCoverUrl) || "/assets/images/blog/blog-img-01.png";

  return (
    <div className="col-lg-12">
      <div className="dashboard-inner admin-blog-page">
        <div className="admin-blog-header mb-30">
          <div>
            <h3>{editingId ? "Edit Blog" : "Blog Publisher"}</h3>
            <p>Create polished, high-converting blog posts with a live preview panel.</p>
          </div>
          <div className="admin-blog-stats">
            <div className="stat-card">
              <span>Total</span>
              <strong>{stats.total}</strong>
            </div>
            <div className="stat-card">
              <span>Published</span>
              <strong>{stats.published}</strong>
            </div>
            <div className="stat-card">
              <span>Draft</span>
              <strong>{stats.draft}</strong>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-4 mb-30">
            <div className="col-lg-7">
              <div className="admin-blog-panel">
                <div className="panel-title">
                  <h5>Post Details</h5>
                </div>
                <div className="row g-3">
                  <div className="col-12">
                    <div className="form-inner">
                      <label>Title*</label>
                      <input
                        value={form.title}
                        onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                        placeholder="Write a compelling headline"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-inner">
                      <label>Category</label>
                      <input
                        value={form.category}
                        onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                        placeholder="Interview Tips"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-inner">
                      <label>Author</label>
                      <input
                        value={form.authorName}
                        onChange={(e) => setForm((prev) => ({ ...prev, authorName: e.target.value }))}
                        placeholder="Author name"
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-inner">
                      <label>Cover Image (Browse from PC)</label>
                      <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleCoverFileChange} />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-inner">
                      <label>Tags (comma separated)</label>
                      <input
                        value={form.tags}
                        onChange={(e) => setForm((prev) => ({ ...prev, tags: e.target.value }))}
                        placeholder="career, interview, nursing"
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-inner">
                      <label>Excerpt*</label>
                      <textarea
                        rows={3}
                        value={form.excerpt}
                        onChange={(e) => setForm((prev) => ({ ...prev, excerpt: e.target.value }))}
                        placeholder="Short summary shown in blog cards"
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-inner">
                      <label>Content*</label>
                      <textarea
                        rows={10}
                        value={form.content}
                        onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                        placeholder="Write rich content with clean paragraph breaks"
                      />
                    </div>
                  </div>
                </div>
                <div className="admin-blog-actions">
                  <div className="status-switch">
                    <input
                      type="checkbox"
                      id="isPublished"
                      checked={form.isPublished}
                      onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))}
                    />
                    <label htmlFor="isPublished">{form.isPublished ? "Published" : "Draft Mode"}</label>
                  </div>
                  <div className="btn-wrap">
                    {editingId ? (
                      <button
                        type="button"
                        className="primry-btn-1 lg-btn"
                        onClick={() => {
                          setEditingId("");
                          setForm(initialForm);
                          if (previewCoverUrl && previewCoverUrl.startsWith("blob:")) {
                            URL.revokeObjectURL(previewCoverUrl);
                          }
                          setPreviewCoverUrl("");
                          setSelectedCoverFile(null);
                        }}
                      >
                        Cancel
                      </button>
                    ) : null}
                    <button type="submit" className="primry-btn-2 lg-btn" disabled={saving}>
                      {saving ? "Saving..." : submitLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="admin-blog-preview panel-shadow">
                <h5>Live Preview</h5>
                <div className="preview-image-wrap">
                  <img src={previewImage} alt="Blog cover preview" />
                  <span className={`preview-status ${form.isPublished ? "published" : "draft"}`}>
                    {form.isPublished ? "Published" : "Draft"}
                  </span>
                </div>
                <div className="preview-content">
                  <div className="preview-meta">
                    <span>{form.authorName || "System Admin"}</span>
                    <span>{previewReadMins} min read</span>
                  </div>
                  <h4>{form.title || "Your blog title appears here"}</h4>
                  <p>{form.excerpt || "A short excerpt helps users decide to open the article."}</p>
                  <div className="preview-tags">
                    {parsedTags.length === 0 ? (
                      <span className="tag-chip muted">No tags</span>
                    ) : (
                      parsedTags.map((tag) => (
                        <span className="tag-chip" key={tag}>
                          {tag}
                        </span>
                      ))
                    )}
                  </div>
                  <div className="preview-foot">
                    <span>{form.category || "General"}</span>
                    <span>{previewReadMins} min read</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="table-wrapper2 blog-table-wrap">
          <div className="title-and-btn">
            <div className="title">
              <h4>Published Content Library</h4>
            </div>
          </div>
          <table className="eg-table table job-list-table mb-0">
            <thead>
              <tr>
                <th>Blog</th>
                <th>Category</th>
                <th>Status</th>
                <th>Published</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {blogs.length === 0 ? (
                <tr>
                  <td colSpan="5">No blogs found.</td>
                </tr>
              ) : (
                blogs.map((blog) => (
                  <tr key={blog._id}>
                    <td>
                      <div className="blog-row-title">
                        <img
                          src={resolveMediaUrl(blog.coverImage) || "/assets/images/blog/blog-img-01.png"}
                          alt={blog.title}
                        />
                        <div>
                          <h6>{blog.title}</h6>
                          <p>{blog.authorName || "System Admin"}</p>
                        </div>
                      </div>
                    </td>
                    <td>{blog.category || "General"}</td>
                    <td>
                      <span className={`table-status ${blog.isPublished ? "ok" : "hold"}`}>
                        {blog.isPublished ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td>{formatDate(blog.publishedAt)}</td>
                    <td>
                      <div className="table-action-btns">
                        <button className="primry-btn-1" onClick={() => handleEdit(blog)}>
                          Edit
                        </button>
                        <button className="primry-btn-1 danger" onClick={() => handleDelete(blog._id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogs;
